import type { FC, PropsWithChildren } from 'react'
import { useState } from 'react'
import styled from '@emotion/styled'
import { css } from '@emotion/react'

import ButtonText from '@segment/matcha/components/ButtonText'
import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'

import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'

import type { CustomerThumbnail } from '@segment/api/routes/customers/queries'

import CustomerCardBase, * as CustomerCard from '../CustomerCard'

interface CustomerCardsGridProps {
  integrations: CustomerThumbnail[]
  nextPageTrigger?: JSX.Element
}

const CustomerCardsGrid: FC<PropsWithChildren<CustomerCardsGridProps>> = ({ integrations, nextPageTrigger }) => (
  <Wrapper>
    <CardsGrid>
      {integrations.map((item, i) => (
        <GridItem key={(item.company ?? ``) + i} area={i % 8} integration={item} />
      ))}
    </CardsGrid>

    {integrations.length === 0 && (
      <CopyTextBase centered={true}>
        <CopyText.Heading tag="h4" variant="h4">
          Sorry, no results found.
        </CopyText.Heading>
        <CopyText.Description tag="p" variant="pMedium">
          Please try selecting different filters.
        </CopyText.Description>
      </CopyTextBase>
    )}

    {nextPageTrigger}
  </Wrapper>
)

type GridItemProps = {
  area: number
  integration: CustomerThumbnail
}
const GridItem: FC<GridItemProps> = ({ area, integration }) => {
  const [isHover, setIsHover] = useState(false)

  return (
    <GridItemWrapper area={area} onMouseEnter={() => setIsHover(true)} onMouseLeave={() => setIsHover(false)}>
      <CustomerCardBase href={`/customers/${integration.slug}/`}>
        <CustomerCard.Logo src={integration.logoUrl} />
        <CustomerCard.Content>{integration.title}</CustomerCard.Content>
        <CustomerCard.Footer>
          <ButtonText size="small" variant="mono" hover={isHover} iconEnd={ChevronRightIcon}>
            Read their story
          </ButtonText>
        </CustomerCard.Footer>
      </CustomerCardBase>
    </GridItemWrapper>
  )
}

const Wrapper = styled.div`
  display: grid;
  padding: 80px 24px 160px 24px;
  gap: 32px;
`

const CardsGrid = styled.div`
  display: grid;
  gap: 32px;

  ${({ theme }) => theme.breakpoints.up(`md`)} {
    grid-template-columns: repeat(2, 1fr);
  }

  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    grid-template-columns: repeat(6, 1fr);
  }
`

type GridItemWrapperProps = {
  area: GridItemProps[`area`]
}

const GridItemWrapper = styled.div<GridItemWrapperProps>`
  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    ${({ area }) =>
      (area === 0 || area === 4) &&
      css`
        grid-column: 1 / 4;
      `}

    ${({ area }) =>
      (area === 1 || area === 5) &&
      css`
        grid-column: 4 / 7;
      `}

    ${({ area }) =>
      area === 2 &&
      css`
        grid-column: 1 / 5;
      `}

    ${({ area }) =>
      area === 3 &&
      css`
        grid-column: 5 / 7;
      `}

    ${({ area }) =>
      area === 6 &&
      css`
        grid-column: 1 / 3;
      `}

    ${({ area }) =>
      area === 7 &&
      css`
        grid-column: 3 / 7;
      `}
  }
`

export default CustomerCardsGrid
